// Vista previa rápida de un cuestionario CAD/CEP — render minimalista de los
// bloques y preguntas con los mismos componentes que ve el estudiante, dentro
// de un modal. Permite al admin previsualizar sin tener que loguearse como
// estudiante ni asignar el cuestionario.
import { useState } from "react";
import { X, ChevronLeft, ChevronRight, AlertCircle } from "lucide-react";
import ImageInteractiveCanvas from "@/components/ImageInteractiveCanvas";
import StackedOperation from "@/components/StackedOperation";
import { RenderTextWithMath } from "@/components/MathFormulaEditor";
import { TablePlayer } from "@/components/QuestionTable";

export default function QuickPreviewModal({ template, onClose }) {
  const blocks = template.blocks || [];
  const allQuestions = blocks.flatMap((b, bi) =>
    (b.questions || []).map((q) => ({ ...q, _blockTitle: b.title, _blockIntro: b.intro, _blockMotiv: b.motivational_phrase, _bi: bi }))
  );
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});

  if (allQuestions.length === 0) {
    return (
      <Wrap onClose={onClose}>
        <div className="p-10 text-center text-sm text-[#6E7591]">
          <AlertCircle className="inline mr-1.5" size={14} />
          Esta plantilla aún no tiene preguntas. Agrega al menos una desde el editor.
        </div>
      </Wrap>
    );
  }

  const q = allQuestions[idx];
  const prevQ = idx > 0 ? allQuestions[idx - 1] : null;
  const showBlockHeader = !prevQ || prevQ._bi !== q._bi;
  const val = answers[q.id];
  const setVal = (v) => setAnswers((a) => ({ ...a, [q.id]: v }));

  return (
    <Wrap onClose={onClose} title={template.title} subtitle={`Vista previa · ${idx + 1} / ${allQuestions.length}`}>
      <div className="space-y-4">
        {showBlockHeader && (
          <div className="space-y-2">
            <p className="text-[10px] tracking-[0.18em] uppercase font-extrabold text-[#A43790]">{q._blockTitle}</p>
            {q._blockMotiv && (
              <p className="text-xs text-[#6E7591] italic">{q._blockMotiv}</p>
            )}
            {q._blockIntro && (
              <div className="bg-[#F7F8FA] border-l-4 border-[#A43790] rounded-r-xl p-3">
                <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790] mb-1">Lectura del bloque</p>
                <p className="whitespace-pre-line text-sm leading-relaxed text-[#1F2547] italic">{q._blockIntro}</p>
              </div>
            )}
          </div>
        )}

        <h3 className="font-display text-xl font-extrabold text-[#1F2547]">
          <RenderTextWithMath text={q.text} />
        </h3>
        {q.domain && (
          <span className="jasq-pill bg-[#FFF7DA] text-[#9C7A18] text-[10px]">{q.domain}</span>
        )}

        {q.image_url && q.type !== "image_interactive" && (
          <img src={q.image_url} alt="" className="max-h-72 rounded-2xl border-2 border-[#E1E5F0] bg-white" />
        )}

        {q.type === "multiple_choice" && (
          <div className="space-y-2">
            {(q.options || []).map((o, i) => {
              const active = val === o.value;
              return (
                <button key={i} type="button" onClick={() => setVal(o.value)}
                  className={`block w-full text-left px-4 py-3 rounded-xl border-2 ${active ? "border-[#A43790] bg-[#FFF1F8]" : "border-[#E1E5F0] hover:border-[#A43790] bg-white"} text-sm font-bold`}>
                  <span className="inline-block w-6 h-6 rounded-full bg-[#1F2547] text-white text-xs font-extrabold text-center leading-6 mr-2">
                    {String.fromCharCode(65 + i)}
                  </span>
                  <RenderTextWithMath text={o.label || ""} />
                  {o.is_correct && <span className="ml-2 text-[10px] text-[#3FA84D] font-extrabold">✓ correcta</span>}
                </button>
              );
            })}
          </div>
        )}

        {(q.type === "numeric" || q.type === "fill_in_blank") && (
          <input type={q.type === "numeric" ? "number" : "text"}
            value={val ?? ""} onChange={(e) => setVal(e.target.value)}
            className="max-w-xs px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] text-base" />
        )}

        {q.type === "likert" && (
          <LikertPreview q={q} value={val} onChange={setVal} />
        )}

        {q.type === "scale_1_10" && (
          <Scale10Preview q={q} value={val} onChange={setVal} />
        )}

        {q.type === "open" && (
          <textarea rows={3} value={val ?? ""} onChange={(e) => setVal(e.target.value)}
            placeholder="Escribe tu respuesta…"
            className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790]" />
        )}

        {q.type === "multi_part" && (
          <MultiPartPreview q={q} value={val || {}} onChange={setVal} />
        )}

        {q.type === "table_input" && (
          <TablePlayer q={q} value={val || {}} onChange={setVal} />
        )}

        {q.type === "stacked_op" && Array.isArray(q.operands) && (
          <div className="flex flex-col items-center gap-3 py-2">
            <StackedOperation
              operands={q.operands}
              operator={q.operator || "+"}
              size="md"
              value={val ?? ""}
              onChange={setVal}
              testId={`preview-stacked-${q.id}`}
            />
          </div>
        )}

        {q.type === "equation" && (
          <div className="space-y-2 max-w-md">
            {q.equation_template && <p className="text-sm bg-[#F7F8FA] rounded px-3 py-2 font-mono border border-[#E1E5F0]">{q.equation_template}</p>}
            <input value={val ?? ""} onChange={(e) => setVal(e.target.value)}
              placeholder="x = …" className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] font-mono" />
          </div>
        )}

        {q.type === "image_interactive" && q.image_url && (
          <ImageInteractiveCanvas
            imageUrl={q.image_url}
            drawingTools={q.drawing_tools && q.drawing_tools.length ? q.drawing_tools : ["brush-red", "eraser"]}
            value={val}
            onChange={setVal}
          />
        )}

        {q.type === "image_interactive" && !q.image_url && (
          <div className="border-2 border-dashed border-[#E1E5F0] rounded-xl p-4 text-center text-[#6E7591] text-sm">
            <AlertCircle className="inline mr-1" size={14} />
            Falta subir la imagen interactiva.
          </div>
        )}

        {q.type === "open_text" && (
          <textarea rows={3} value={val ?? ""} onChange={(e) => setVal(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790]" />
        )}

        {q.correct_answer && (q.type === "numeric" || q.type === "fill_in_blank" || q.type === "equation") && (
          <p className="text-[11px] text-[#3FA84D] font-bold">Respuesta correcta esperada: <span className="font-mono">{q.correct_answer}</span></p>
        )}
      </div>

      <div className="mt-6 pt-4 border-t-2 border-[#E1E5F0] flex items-center justify-between gap-3">
        <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}
          className="jasq-btn-ghost inline-flex items-center gap-1 disabled:opacity-30" data-testid="preview-prev">
          <ChevronLeft size={14} /> Anterior
        </button>
        <p className="text-[11px] text-[#6E7591]">Pregunta {idx + 1} de {allQuestions.length}</p>
        <button onClick={() => setIdx(Math.min(allQuestions.length - 1, idx + 1))} disabled={idx === allQuestions.length - 1}
          className="jasq-btn-primary inline-flex items-center gap-1" data-testid="preview-next">
          Siguiente <ChevronRight size={14} />
        </button>
      </div>
    </Wrap>
  );
}

function Wrap({ onClose, title, subtitle, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="quick-preview-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E1E5F0] flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790]">Vista previa rápida</p>
            <h3 className="font-display text-lg font-extrabold text-[#1F2547]">{title}</h3>
            {subtitle && <p className="text-xs text-[#6E7591]">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]" data-testid="preview-close"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 max-h-[75vh] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

// ----- Helpers de preview para Likert / Escala / Multi-parte -----
function LikertPreview({ q, value, onChange }) {
  const labels = q.scale_labels || {};
  const min = 1, max = 5;
  return (
    <div className="space-y-2" data-testid={`preview-likert-${q.id}`}>
      <div className="flex justify-between text-[11px] font-bold text-[#6E7591] px-1">
        <span>{labels.min || `Mínimo (${min})`}</span>
        <span>{labels.max || `Máximo (${max})`}</span>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {[1, 2, 3, 4, 5].map((n) => {
          const active = value === n;
          return (
            <button key={n} type="button" onClick={() => onChange(n)}
              data-testid={`preview-likert-${q.id}-${n}`}
              className={`px-3 py-3 rounded-xl border-2 text-sm font-extrabold transition-colors ${active ? "border-[#A43790] bg-[#A43790] text-white" : "border-[#E1E5F0] hover:border-[#A43790] bg-white text-[#1F2547]"}`}>
              {n}
            </button>
          );
        })}
      </div>
      {labels.mid && <p className="text-[11px] text-[#6E7591] italic text-center">Centro: {labels.mid}</p>}
      {q.inverted && (
        <p className="text-[11px] text-[#9C7A18] bg-[#FFF7DA] border border-[#F5B82E] rounded px-2 py-1 inline-block font-bold">
          ⚠ Pregunta invertida — se puntúa al revés
        </p>
      )}
    </div>
  );
}

function Scale10Preview({ q, value, onChange }) {
  const labels = q.scale_labels || {};
  return (
    <div className="space-y-2" data-testid={`preview-scale10-${q.id}`}>
      <div className="flex justify-between text-[11px] font-bold text-[#6E7591] px-1">
        <span>{labels.min || "1"}</span>
        <span>{labels.max || "10"}</span>
      </div>
      <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
          const active = value === n;
          return (
            <button key={n} type="button" onClick={() => onChange(n)}
              data-testid={`preview-scale10-${q.id}-${n}`}
              className={`aspect-square rounded-lg border-2 text-sm font-extrabold transition-colors ${active ? "border-[#1F2547] bg-[#1F2547] text-white" : "border-[#E1E5F0] hover:border-[#1F2547] bg-white text-[#1F2547]"}`}>
              {n}
            </button>
          );
        })}
      </div>
      {q.inverted && (
        <p className="text-[11px] text-[#9C7A18] bg-[#FFF7DA] border border-[#F5B82E] rounded px-2 py-1 inline-block font-bold">
          ⚠ Pregunta invertida — se puntúa al revés
        </p>
      )}
    </div>
  );
}

function MultiPartPreview({ q, value, onChange }) {
  const subs = q.sub_questions || [];
  if (subs.length === 0) {
    return (
      <p className="text-xs italic text-[#A52258] bg-[#FFE9F0] rounded p-2">
        Esta pregunta multi-parte no tiene sub-preguntas configuradas.
      </p>
    );
  }
  const setSub = (sid, v) => {
    const next = { ...(value || {}) };
    if (v === undefined || v === "" || v === null) delete next[sid];
    else next[sid] = v;
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid={`preview-mp-${q.id}`}>
      {subs.map((s, i) => {
        const label = s.label || String.fromCharCode(65 + i);
        const cur = (value || {})[s.id];
        return (
          <div key={s.id || i} className="rounded-2xl border-2 border-[#E1E5F0] bg-white p-3 space-y-2">
            <div className="flex items-start gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#A43790] text-white text-xs font-extrabold shrink-0">{label}</span>
              <p className="flex-1 text-sm font-bold text-[#1F2547]">{s.text || `Sub-pregunta ${label}`}</p>
            </div>
            {s.type === "multiple_choice" && (
              <div className="flex flex-col gap-1.5 pl-9">
                {(s.options || []).map((o, oi) => {
                  const checked = cur === o.value;
                  return (
                    <button key={oi} type="button" onClick={() => setSub(s.id, o.value)}
                      className={`text-left px-3 py-2 rounded-xl border-2 text-sm ${checked ? "border-[#A43790] bg-[#FFE9F0] text-[#A52258] font-bold" : "border-[#E1E5F0] hover:border-[#A43790]"}`}>
                      <RenderTextWithMath text={o.label || ""} /> {o.is_correct && <span className="text-[10px] text-[#3FA84D] font-extrabold ml-1">✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
            {(s.type === "numeric" || s.type === "fill_in_blank") && (
              <input type={s.type === "numeric" ? "number" : "text"} value={cur ?? ""}
                onChange={(e) => setSub(s.id, e.target.value)}
                className="ml-9 w-[calc(100%-2.25rem)] px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-sm" />
            )}
            {s.type === "open_text" && (
              <textarea rows={2} value={cur ?? ""} onChange={(e) => setSub(s.id, e.target.value)}
                placeholder="Tu respuesta…"
                className="ml-9 w-[calc(100%-2.25rem)] px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-sm" />
            )}
          </div>
        );
      })}
    </div>
  );
}
